<script>
import { mapActions, mapState } from 'vuex';
import * as ReviewElementTypes from '../../variables/reviewElementTypes';
import ReviewApprovalValues from '../../variables/reviewApprovalValues';
import ReviewService from '../../services/review-service';
import ReviewRatingsContainer from './ReviewRatingsContainer';
import ReviewRatingsContainerAlternative from './ReviewRatingsContainerAlternative';
import ReviewStatementContainer from './ReviewStatementContainer';
import ReviewEditContainer from './ReviewEditContainer';
import ReviewInfo from './ReviewInfo';
import ReviewInfoAlternative from './ReviewInfoAlternative';
import ReviewProductInfo from './ReviewProductInfo';

export default {
  name: 'ReviewCard',
  components: {
    ReviewRatingsContainer,
    ReviewStatementContainer,
    ReviewEditContainer,
    ReviewInfo,
    ReviewInfoAlternative,
    ReviewRatingsContainerAlternative,
    ReviewProductInfo,
  },
  props: {
    review: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      required: true,
    },
    approvalStatusFilter: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      name: this.getName(this.review.reviewInformation),
      dateCreated: this.review.endDate,
      averageScore: this.doubleRoundScore(this.review.averageScore),
      originalLanguage: this.review.originalLanguage,
      title: this.getTitle(this.review.reviewInformation),
      description: this.getDescription(this.review.reviewInformation),
      reviewId: this.review.reviewId,
      categoriesShown: false,
      categoryRatings: this.review.categoryRatings,
      manyReviewInformation: this.review.reviewInformation,
      UNRESOLVED: ReviewApprovalValues.UNRESOLVED,
      APPROVED: ReviewApprovalValues.APPROVED,
      DISAPPROVED: ReviewApprovalValues.DISAPPROVED,
      pros: this.getPros(this.review.reviewInformation),
      cons: this.getCons(this.review.reviewInformation),
      editable: true,
      brandId: this.review.reviewBookingInformation[0].brandId,
      brandName: null,
      productInfo: this.review.reviewBookingProduct,
    };
  },
  computed: {
    ...mapState({
      brands: state => state.app.auth.brands,
    }),
  },
  created() {
    this.getBrand();
  },
  methods: {
    ...mapActions({
      approveReview: 'review/approveReview',
      disapproveReview: 'review/disapproveReview',
      updateSelectStatus: 'review/updateSelectStatus',
      fetchApprovalStatusOverview: 'review/fetchApprovalStatusOverview',
    }),
    doubleRoundScore(score) {
      return ReviewService.doubleRoundScore(score);
    },
    getName(manyReviewInformation) {
      return manyReviewInformation.filter(
        element => element.type === ReviewElementTypes.REVIEW_NAME ||
        element.type === ReviewElementTypes.REVIEW_NAME_DK,
      )[0];
    },
    getTitle(manyReviewInformation) {
      return manyReviewInformation.filter(
        element => element.type === ReviewElementTypes.REVIEW_TITLE,
      )[0];
    },
    getDescription(manyReviewInformation) {
      return manyReviewInformation.filter(
        element => element.type === ReviewElementTypes.REVIEW_DESCRIPTION ||
        element.type === ReviewElementTypes.REVIEW_DESCRIPTION_DK ||
        element.type === ReviewElementTypes.REVIEW_DESCRIPTION_VS,
      )[0];
    },
    getPros(manyReviewInformation) {
      return manyReviewInformation.filter(
        element => element.type === ReviewElementTypes.REVIEW_PROS,
      );
    },
    getCons(manyReviewInformation) {
      return manyReviewInformation.filter(
        element => element.type === ReviewElementTypes.REVIEW_CONS,
      );
    },
    toggleRatings() {
      this.categoriesShown = !this.categoriesShown;
    },
    confirmApproveReview() {
      if (this.approvalStatusFilter === this.UNRESOLVED) {
        this.approveReview(this.reviewId)
          .then(() => {
            this.fetchApprovalStatusOverview();
          });
      } else {
        // eslint-disable-next-line no-alert,no-lonely-if
        if (confirm(this.$t('review.reviewCard.confirmMessage'))) {
          this.approveReview(this.reviewId)
            .then(() => {
              this.fetchApprovalStatusOverview();
            });
        }
      }
    },
    confirmDisapproveReview() {
      if (this.approvalStatusFilter === this.UNRESOLVED) {
        this.disapproveReview(this.reviewId)
          .then(() => {
            this.fetchApprovalStatusOverview();
          });
      } else {
        // eslint-disable-next-line no-alert,no-lonely-if
        if (confirm(this.$t('review.reviewCard.confirmMessage'))) {
          this.disapproveReview(this.reviewId)
            .then(() => {
              this.fetchApprovalStatusOverview();
            });
        }
      }
    },
    getBrand() {
      const brand = this.brands.filter(element => element.id === this.brandId)[0];
      this.brandName = brand.name || null;
    },
  },
};
</script>

<template>
  <div>
    <v-card class="mb-3 elevation-3">
      <v-card-title>
        <v-flex xs5>
          <v-tooltip left>
            <template v-slot:activator="{ on: tooltip }">
              <h3 v-if="name" class="headline mb-0" v-on="tooltip">{{ name.text }}</h3>
              <h3
                v-else
                class="headline mb-0"
                v-on="tooltip"
              >
                {{ $t('review.reviewCard.noName') }}
              </h3>
            </template>
            <span>{{ $t('review.reviewCard.tooltip.name') }}</span>
          </v-tooltip>
        </v-flex>
        <v-flex>
          <v-chip color="light-gray">
            {{ brandName }}
          </v-chip>
        </v-flex>
        <v-flex>
          <v-tooltip left>
            <template v-slot:activator="{ on: tooltip }">
              <h4 v-on="tooltip">{{ dateCreated }}</h4>
            </template>
            <span>{{ $t('review.reviewCard.tooltip.dateCreated') }}</span>
          </v-tooltip>
        </v-flex>
        <v-flex v-if="originalLanguage !== 'SEE IR-159'">
          <h4>
            {{ originalLanguage }}
          </h4>
        </v-flex>
        <v-flex>
          <v-tooltip left>
            <template v-slot:activator="{ on: tooltip }">
              <v-chip
                class="font-weight-bold"
                color="primary"
                text-color="white"
                v-on="tooltip"
              >
                {{ averageScore }}
              </v-chip>
            </template>
            <span>{{ $t('review.reviewCard.tooltip.averageScore') }}</span>
          </v-tooltip>
        </v-flex>
        <v-flex>
          <review-edit-container
            :name="name"
            :title="title"
            :description="description"
            :pros="pros"
            :cons="cons"
            :reviewId="reviewId"
          />
        </v-flex>
      </v-card-title>
      <v-divider class="mx-3"/>
          <review-info
             :title="title"
             :description="description"
          />
      <review-statement-container
        :pros="pros"
        :cons="cons"
      />
      <v-divider v-if="productInfo" class="mx-3"/>
      <review-product-info
        v-if="productInfo"
        :product="productInfo"
      />
      <v-divider class="mx-3"/>
      <div v-if="categoriesShown">
        <review-ratings-container
          :category-ratings="categoryRatings"
          :many-review-information="manyReviewInformation"
        />
      </div>
      <v-card-actions>
        <v-flex xs10>
          <v-btn
            @click="confirmApproveReview"
            :disabled="approvalStatusFilter === APPROVED"
            color="green"
            flat
          >
            {{ $t('review.reviewCard.approve') }}
            <v-icon>done</v-icon>
          </v-btn>
          <v-btn
            @click="confirmDisapproveReview"
            :disabled="approvalStatusFilter === DISAPPROVED"
            color="red"
            flat
          >
            {{ $t('review.reviewCard.disapprove') }}
            <v-icon>clear</v-icon>
          </v-btn>
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <v-btn @click="updateSelectStatus(reviewId)" flat v-on="on">
                <v-icon v-if="selected" color="success">check_box</v-icon>
                <v-icon v-else color="black">check_box_outline_blank</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('review.reviewCard.tooltip.select') }}</span>
          </v-tooltip>
        </v-flex>
        <v-flex>
          <v-btn
            @click="toggleRatings"
            flat color="primary"
          >
            <h5 v-if="!categoriesShown">{{ $t('review.reviewCard.showRatings') }}</h5>
            <h5 v-else>{{ $t('review.reviewCard.hideRatings') }}</h5>
            <v-icon v-if="!categoriesShown">keyboard_arrow_down</v-icon>
            <v-icon v-else>keyboard_arrow_up</v-icon>
          </v-btn>
        </v-flex>
      </v-card-actions>
    </v-card>
  </div>
</template>



// WEBPACK FOOTER //
// src/components/review/ReviewCard.vue